import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authApi, authStorage, type LoginData, type RegisterData, type ForgotPasswordData, type ResetPasswordData } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import logger from '@/utils/logger';

export function useAuth() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [hasToken, setHasToken] = useState(() => !!authStorage.getToken());

  // Listen for token changes
  useEffect(() => {
    const checkToken = () => {
      setHasToken(!!authStorage.getToken());
    };
    
    // Check on mount and storage changes
    checkToken();
    
    // Listen for storage events (for cross-tab updates)
    window.addEventListener('storage', checkToken);
    
    return () => {
      window.removeEventListener('storage', checkToken);
    };
  }, []);

  // Get current user
  const { data: user, isLoading } = useQuery({
    queryKey: ["/api/auth/me"],
    queryFn: authApi.getCurrentUser,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: hasToken, // Only fetch if token exists
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      try {
        authStorage.setToken(data.token);
        authStorage.setUser(data.user);
        setHasToken(true);
        queryClient.setQueryData(["/api/auth/me"], data.user);
        
        // Use setTimeout to ensure toast shows before navigation
        setTimeout(() => {
          toast({
            title: "Welcome back!",
            description: "You have successfully logged in.",
          });
        }, 100);
        
        // Check for redirect after login
        const redirectPath = localStorage.getItem('redirectAfterLogin');
        if (redirectPath) {
          localStorage.removeItem('redirectAfterLogin');
          setTimeout(() => navigate(redirectPath), 500);
        } else {
          setTimeout(() => navigate("/"), 500); // Default to home
        }
      } catch (error) {
        logger.error('Login success handler error:', error);
        // Fallback to basic navigation if there's an error
        navigate("/");
      }
    },
    onError: (error: any) => {
      logger.error('Login error:', error);
      toast({
        title: "Login failed",
        description: error.message || "Invalid credentials",
        variant: "destructive",
      });
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: authApi.register,
    onSuccess: (data) => {
      toast({
        title: "Account created!",
        description: "Please check your email to verify your account.",
      });
      navigate("/login"); // Redirect to login
    },
    onError: (error: any) => {
      toast({
        title: "Registration failed",
        description: error.message || "Failed to create account",
        variant: "destructive",
      });
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      const isMockMode = import.meta.env.VITE_APP_MOCK_DATA === "true";
      
      if (!isMockMode) {
        authStorage.removeToken();
        authStorage.removeUser();
      }      
      setHasToken(false);
      queryClient.setQueryData(["/api/auth/me"], null);
      queryClient.clear();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
      navigate("/");
    },
    onError: (error: any) => {
      const isMockMode = import.meta.env.VITE_APP_MOCK_DATA === "true";
      
      // Force logout even if API call fails
      if (!isMockMode) {
        authStorage.removeToken();
        authStorage.removeUser();
      }      
      setHasToken(false);
      queryClient.setQueryData(["/api/auth/me"], null);
      queryClient.clear();
      navigate("/");
    },
  });

  // Forgot password mutation
  const forgotPasswordMutation = useMutation({
    mutationFn: authApi.forgotPassword,
    onSuccess: () => {
      toast({
        title: "Reset link sent",
        description: "Please check your email for password reset instructions.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to send reset link",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: authApi.resetPassword,
    onSuccess: () => {
      toast({
        title: "Password reset successful",
        description: "Your password has been updated. Please login with your new password.",
      });
      navigate("/login");
    },
    onError: (error: any) => {
      toast({
        title: "Reset failed",
        description: error.message || "Invalid or expired reset token",
        variant: "destructive",
      });
    },
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout: logoutMutation.mutate,
    forgotPassword: forgotPasswordMutation.mutate,
    resetPassword: resetPasswordMutation.mutate,
    isLoginPending: loginMutation.isPending,
    isRegisterPending: registerMutation.isPending,
    isLogoutPending: logoutMutation.isPending,
    isForgotPasswordPending: forgotPasswordMutation.isPending,
    isResetPasswordPending: resetPasswordMutation.isPending,
  };
}